import React, { useContext, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import { LoaderCircle, SquareArrowOutUpRight } from "lucide-react";

import { cn } from "utils";
import { getIntegrityRiskStyles } from "utils/helpers/assessments";
import { getPlatformProfileUrl } from "utils/helpers/users";
import { AlertContext, JobsContext } from "context/providers";
import { GET_CANDIDATE_ASSESSMENTS_FOR_JOB } from "api/graphql/jobOpportunity";
import {
  ASSESSMENT_SENDER_LABEL,
  formatAssessmentSender,
} from "components/UserCardList/helpers/userCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";

// Human-readable label for the SharedAssessment lifecycle status enum.
const formatTrackingStatus = (status) => {
  switch (status) {
    case "ASSESSMENT_SENT":
      return "Assigned";
    case "ASSESSMENT_STARTED":
      return "Started";
    case "ASSESSMENT_EXPIRED":
      return "Expired";
    case "ASSESSMENT_COMPLETED":
      return "Completed";
    default:
      return status || "-";
  }
};

// Colour treatment for the tracking status badge.
const getTrackingStatusStyles = (status) => {
  switch (status) {
    case "ASSESSMENT_STARTED":
      return "bg-sky-50 text-sky-700 border-sky-100";
    case "ASSESSMENT_EXPIRED":
      return "bg-rose-50 text-rose-700 border-rose-100";
    case "ASSESSMENT_COMPLETED":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    default:
      return "bg-slate-50 text-slate-700 border-slate-100";
  }
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const PANEL_CLASSNAME =
  "space-y-3 rounded-lg border border-slate-300 bg-surface p-6";
const PANEL_TITLE_CLASSNAME =
  "font-inter text-base font-semibold text-slate-700";

// The candidate name cell, shared between the results and tracking rows.
const renderCandidateCell = (user = {}) => {
  const name = (
    <>
      <span className="block leading-tight">{user.given_name || "-"}</span>
      <span className="block leading-tight">{user.family_name || ""}</span>
    </>
  );

  return (
    <TableCell className="whitespace-nowrap bg-white font-medium">
      {user.email ? (
        <a
          href={`mailto:${user.email}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          {name}
        </a>
      ) : (
        name
      )}
    </TableCell>
  );
};

// The candidate username cell, shared between the results and tracking rows.
const renderUsernameCell = (user = {}) => (
  <TableCell className="whitespace-nowrap bg-white">
    {user.username ? (
      <a
        href={getPlatformProfileUrl(user.username)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
      >
        @{user.username}
        <SquareArrowOutUpRight className="h-3.5 w-3.5" />
      </a>
    ) : (
      "-"
    )}
  </TableCell>
);

// The "Candidate Assessments" tab: a read-only table of the assessments taken
// by candidates for this job. Fetches its own data when it mounts (i.e. when
// the dialog opens on this tab).
const CandidateAssessmentsTab = () => {
  const { jobOpp } = useContext(JobsContext);
  const { addGraphQLAlert } = useContext(AlertContext);

  const [fetchCandidateAssessments, { data, loading }] = useLazyQuery(
    GET_CANDIDATE_ASSESSMENTS_FOR_JOB,
    {
      fetchPolicy: "network-only",
      onError: (error) => addGraphQLAlert({ errors: error?.graphQLErrors }),
    }
  );

  // Fetch once, when the tab first mounts.
  const hasFetchedRef = useRef(false);
  if (!hasFetchedRef.current && jobOpp?.id) {
    hasFetchedRef.current = true;
    fetchCandidateAssessments({
      variables: { jobOpportunityId: jobOpp.id },
    });
  }

  const assessments = data?.getCandidateAssessmentsForJob || [];

  // Completed assessments carry an `assessmentResult`; ones that were only sent,
  // started or expired carry an `assessmentTracking` record instead.
  const results = assessments.filter((entry) => entry?.assessmentResult);
  const tracking = assessments.filter((entry) => entry?.assessmentTracking);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4">
      <p className="mt-3 font-inter text-xs font-normal text-slate-700">
        Assessments taken by candidates for this job, along with their scores
        and integrity risk.
      </p>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-1">
        {loading ? (
          <div className="flex items-center gap-x-2 text-sm text-gray-500">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            Loading candidate assessments...
          </div>
        ) : assessments.length > 0 ? (
          <>
            {results.length > 0 && (
              <section className={PANEL_CLASSNAME}>
                <h3 className={PANEL_TITLE_CLASSNAME}>Completed Assessments</h3>

                <div className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="sticky top-0 z-10">
                        <TableHead className="whitespace-nowrap">
                          Candidate
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Username
                        </TableHead>
                        <TableHead className="w-full">Assessment</TableHead>
                        <TableHead className="whitespace-nowrap">
                          Score
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Risk
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Taken At
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          {ASSESSMENT_SENDER_LABEL}
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-right">
                          View Result
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.map((entry, index) => {
                        const user = entry?.user || {};
                        const result = entry?.assessmentResult || {};
                        const risk = result.integrityReport?.risk;

                        return (
                          <TableRow
                            key={`result-${
                              user.username || user.email || "candidate"
                            }-${result.testName || index}`}
                          >
                            {renderCandidateCell(user)}
                            {renderUsernameCell(user)}
                            <TableCell className="w-full bg-white font-medium">
                              {result.testName || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white">
                              {result.score ?? "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white">
                              {risk ? (
                                <span
                                  className={cn(
                                    "inline-flex rounded border px-2 py-0.5 text-xs font-bold uppercase",
                                    getIntegrityRiskStyles(risk)
                                  )}
                                >
                                  {risk}
                                </span>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white">
                              {formatDate(result.completedAt)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white">
                              {formatAssessmentSender(entry?.senderEmail)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white text-right">
                              {result.previewLink ? (
                                <a
                                  href={result.previewLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                                >
                                  View
                                  <SquareArrowOutUpRight className="h-3.5 w-3.5" />
                                </a>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </section>
            )}

            {tracking.length > 0 && (
              <section className={PANEL_CLASSNAME}>
                <h3 className={PANEL_TITLE_CLASSNAME}>
                  Assessments Sent, Started or Expired
                </h3>

                <div className="overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="sticky top-0 z-10">
                        <TableHead className="whitespace-nowrap">
                          Candidate
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Username
                        </TableHead>
                        <TableHead className="w-full">Assessment</TableHead>
                        <TableHead className="whitespace-nowrap">
                          Status
                        </TableHead>
                        <TableHead className="whitespace-nowrap">
                          Status Updated
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tracking.map((entry, index) => {
                        const user = entry?.user || {};
                        const record = entry?.assessmentTracking || {};

                        return (
                          <TableRow
                            key={`tracking-${
                              user.username || user.email || "candidate"
                            }-${record.testName || index}`}
                          >
                            {renderCandidateCell(user)}
                            {renderUsernameCell(user)}
                            <TableCell className="w-full bg-white font-medium">
                              {record.testName || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white">
                              {record.status ? (
                                <span
                                  className={cn(
                                    "inline-flex rounded border px-2 py-0.5 text-xs font-bold uppercase",
                                    getTrackingStatusStyles(record.status)
                                  )}
                                >
                                  {formatTrackingStatus(record.status)}
                                </span>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap bg-white">
                              {formatDate(record.statusUpdatedAt)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </section>
            )}
          </>
        ) : (
          <p className="font-inter text-sm font-normal text-gray-500">
            No candidate assessments taken for this job yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default CandidateAssessmentsTab;
